import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/seo";
import { FiTrendingUp, FiUser, FiUsers, FiHome, FiDollarSign, FiInfo } from "@/lib/icons";
import { ToggleGroup } from "./_components/ToggleGroup";
import { useBorrowingPowerCalculator } from "./_hooks/useBorrowingPowerCalculator";
import { cn } from "@/lib/utils";
import type { HouseholdType, Location } from "./_data/hemBenchmarks";

/**
 * Format as AUD currency
 */
function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Slider with currency display
 */
function CurrencySlider({
  label,
  value,
  onChange,
  min,
  max,
  step,
  presets,
  tooltip,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step: number;
  presets?: { value: number; label: string }[];
  tooltip?: string;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-foreground">{label}</label>
          {tooltip && (
            <div className="group relative">
              <FiInfo className="size-4 text-muted-foreground cursor-help" />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-foreground text-background text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                {tooltip}
              </div>
            </div>
          )}
        </div>
        <span className="text-lg font-bold text-primary">{formatCurrency(value)}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
      />
      {presets && (
        <div className="flex flex-wrap gap-2">
          {presets.map((p) => (
            <button
              key={p.value}
              onClick={() => onChange(p.value)}
              className={cn(
                "px-3 py-1.5 text-xs font-medium rounded-lg transition-colors",
                value === p.value
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              )}
            >
              {p.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Section card component
 */
function SectionCard({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-background rounded-2xl border border-border p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-cream rounded-lg">{icon}</div>
        <h3 className="font-medium">{title}</h3>
      </div>
      <div className="space-y-6">{children}</div>
    </div>
  );
}

/**
 * Borrowing Power Calculator Page
 */
export function BorrowingPowerCalculatorPage() {
  const { inputs, results, updateInput } = useBorrowingPowerCalculator();

  const householdOptions: { value: HouseholdType; label: string }[] = [
    { value: "single", label: "Single" },
    { value: "couple", label: "Couple" },
  ];

  const locationOptions: { value: Location; label: string }[] = [
    { value: "metro", label: "Metro" },
    { value: "regional", label: "Regional" },
  ];

  const dependentOptions = [
    { value: "0", label: "0" },
    { value: "1", label: "1" },
    { value: "2", label: "2" },
    { value: "3", label: "3" },
    { value: "4+", label: "4+" },
  ];

  const incomePresets = [
    { value: 80000, label: "$80K" },
    { value: 100000, label: "$100K" },
    { value: 150000, label: "$150K" },
    { value: 200000, label: "$200K" },
  ];

  return (
    <>
      <SEO
        title="Borrowing Power Calculator"
        description="Find out how much you could borrow for a home loan based on your income, expenses, and existing debts."
        url="/calculators/borrowing-power"
      />

      {/* Hero */}
      <section className="bg-cream py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <div className="inline-flex p-3 bg-accent/20 rounded-xl mb-6">
              <FiTrendingUp className="size-6 text-primary" />
            </div>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-6">
              Borrowing Power Calculator
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Find out how much you could borrow based on your income, expenses, and financial
              commitments. This calculator uses bank assessment criteria including the 3% APRA
              serviceability buffer.
            </p>
          </div>
        </div>
      </section>

      {/* Calculator Inputs */}
      <section className="py-16 md:py-20 -mt-8">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Income Section */}
            <SectionCard title="Your Income" icon={<FiUser className="size-5 text-primary" />}>
              <CurrencySlider
                label="Gross Annual Income"
                value={inputs.grossIncome}
                onChange={(v) => updateInput("grossIncome", v)}
                min={30000}
                max={500000}
                step={5000}
                presets={incomePresets}
              />
              <CurrencySlider
                label="Partner's Income"
                value={inputs.partnerIncome}
                onChange={(v) => updateInput("partnerIncome", v)}
                min={0}
                max={500000}
                step={5000}
                tooltip="Leave at $0 if single"
              />
              <CurrencySlider
                label="Other Income"
                value={inputs.otherIncome}
                onChange={(v) => updateInput("otherIncome", v)}
                min={0}
                max={100000}
                step={1000}
                tooltip="Rental income, investments, etc."
              />
            </SectionCard>

            {/* Household Section */}
            <SectionCard title="Your Household" icon={<FiUsers className="size-5 text-primary" />}>
              <div className="space-y-3">
                <label className="text-sm font-medium">Household Type</label>
                <ToggleGroup
                  options={householdOptions}
                  value={inputs.householdType}
                  onChange={(v) => updateInput("householdType", v)}
                />
              </div>
              <div className="space-y-3">
                <label className="text-sm font-medium">Location</label>
                <ToggleGroup
                  options={locationOptions}
                  value={inputs.location}
                  onChange={(v) => updateInput("location", v)}
                />
              </div>
              <div className="space-y-3">
                <label className="text-sm font-medium">Dependents</label>
                <ToggleGroup
                  options={dependentOptions}
                  value={inputs.dependents >= 4 ? "4+" : String(inputs.dependents)}
                  onChange={(v) => updateInput("dependents", v === "4+" ? 4 : parseInt(v))}
                />
              </div>
            </SectionCard>

            {/* Debts Section */}
            <SectionCard title="Existing Debts" icon={<FiDollarSign className="size-5 text-primary" />}>
              <CurrencySlider
                label="Credit Card Limits"
                value={inputs.creditCardLimits}
                onChange={(v) => updateInput("creditCardLimits", v)}
                min={0}
                max={50000}
                step={1000}
                tooltip="Total limits, assessed at 3% monthly"
              />
              <CurrencySlider
                label="Existing Loan Repayments"
                value={inputs.existingLoans}
                onChange={(v) => updateInput("existingLoans", v)}
                min={0}
                max={5000}
                step={50}
                tooltip="Monthly car/personal loan payments"
              />
              <CurrencySlider
                label="HECS/HELP Debt"
                value={inputs.hecsDebt}
                onChange={(v) => updateInput("hecsDebt", v)}
                min={0}
                max={100000}
                step={1000}
                tooltip="Repayments calculated based on income"
              />
            </SectionCard>

            {/* Loan Preferences */}
            <SectionCard title="Loan Details" icon={<FiHome className="size-5 text-primary" />}>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Interest Rate</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={1}
                      max={15}
                      step={0.01}
                      value={inputs.interestRate}
                      onChange={(e) => {
                        const value = parseFloat(e.target.value);
                        if (!isNaN(value) && value >= 1 && value <= 15) {
                          updateInput("interestRate", value);
                        }
                      }}
                      className="w-20 px-2 py-1 text-right text-lg font-bold text-primary border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <span className="text-lg font-bold text-primary">%</span>
                  </div>
                </div>
                <input
                  type="range"
                  min={4}
                  max={9}
                  step={0.1}
                  value={inputs.interestRate}
                  onChange={(e) => updateInput("interestRate", parseFloat(e.target.value))}
                  className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                />
                <p className="text-xs text-muted-foreground">
                  Assessment rate: {(inputs.interestRate + 3).toFixed(2)}% (includes 3% APRA buffer)
                </p>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Loan Term</label>
                  <span className="text-lg font-bold text-primary">{inputs.loanTerm} years</span>
                </div>
                <input
                  type="range"
                  min={10}
                  max={30}
                  step={1}
                  value={inputs.loanTerm}
                  onChange={(e) => updateInput("loanTerm", parseInt(e.target.value))}
                  className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                />
                <div className="flex flex-wrap gap-2">
                  {[20, 25, 30].map((term) => (
                    <button
                      key={term}
                      onClick={() => updateInput("loanTerm", term)}
                      className={cn(
                        "px-3 py-1.5 text-xs font-medium rounded-lg transition-colors",
                        inputs.loanTerm === term
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted text-muted-foreground hover:bg-muted/80"
                      )}
                    >
                      {term} years
                    </button>
                  ))}
                </div>
              </div>
            </SectionCard>
          </div>

          {/* Results Summary - placed after inputs */}
          <div className="bg-primary text-primary-foreground rounded-3xl p-8 md:p-10 max-w-3xl mx-auto mt-12">
            <div className="text-center">
              <p className="text-primary-foreground/90 mb-2">
                {results.isManualOverride ? "Your specified loan amount" : "Your estimated borrowing power"}
              </p>
              <p className="text-5xl md:text-6xl font-bold mb-4">
                {formatCurrency(results.maxBorrowing)}
              </p>
              <p className="text-primary-foreground/90 text-sm">
                Based on {results.assessmentRate.toFixed(2)}% assessment rate
                (includes {results.bufferApplied}% buffer)
              </p>
              {results.isManualOverride && (
                <p className="text-accent text-sm mt-2">
                  Calculated borrowing power: {formatCurrency(results.calculatedBorrowing)}
                </p>
              )}
            </div>

            {/* Manual Override Toggle */}
            <div className="mt-6 pt-6 border-t border-primary-foreground/20">
              <div className="flex flex-col items-center gap-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={inputs.manualLoanAmount !== null}
                    onChange={(e) => {
                      if (e.target.checked) {
                        updateInput("manualLoanAmount", results.calculatedBorrowing);
                      } else {
                        updateInput("manualLoanAmount", null);
                      }
                    }}
                    className="w-5 h-5 accent-accent"
                  />
                  <span className="text-sm text-primary-foreground/80">
                    Override with custom loan amount
                  </span>
                </label>
                {inputs.manualLoanAmount !== null && (
                  <div className="w-full max-w-xs">
                    <input
                      type="number"
                      value={inputs.manualLoanAmount || ""}
                      onChange={(e) => updateInput("manualLoanAmount", parseFloat(e.target.value) || 0)}
                      placeholder="Enter loan amount"
                      className="w-full px-4 py-2 rounded-lg bg-white/10 text-primary-foreground text-center text-lg font-semibold border border-primary-foreground/20 focus:outline-none focus:border-accent"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 pt-8 border-t border-primary-foreground/20">
              <div className="text-center">
                <p className="text-primary-foreground/85 text-xs mb-1">Net Monthly Income</p>
                <p className="font-semibold">{formatCurrency(results.monthlyIncome)}</p>
                <p className="text-primary-foreground/80 text-xs">(after tax)</p>
              </div>
              <div className="text-center">
                <p className="text-primary-foreground/85 text-xs mb-1">Living Expenses</p>
                <p className="font-semibold">{formatCurrency(results.monthlyExpenses)}</p>
                <p className="text-primary-foreground/80 text-xs">(HEM benchmark)</p>
              </div>
              <div className="text-center">
                <p className="text-primary-foreground/85 text-xs mb-1">Debt Commitments</p>
                <p className="font-semibold">{formatCurrency(results.monthlyDebtCommitments)}</p>
              </div>
              <div className="text-center">
                <p className="text-primary-foreground/85 text-xs mb-1">Est. Repayment</p>
                <p className="font-semibold">{formatCurrency(results.monthlyRepayment)}/mo</p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 text-center">
            <p className="text-muted-foreground mb-4">
              Want a more accurate assessment based on actual lender criteria?
            </p>
            <Button asChild className="btn-pill bg-accent text-accent-foreground hover:bg-accent/90">
              <Link to="/contact">Get Pre-Approved</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h2 className="font-serif text-2xl mb-6">How is borrowing power calculated?</h2>
          <div className="prose prose-sm max-w-none text-muted-foreground">
            <p className="mb-4">
              Australian lenders assess your borrowing capacity using several factors:
            </p>
            <ul className="space-y-2 list-disc pl-6">
              <li>
                <strong>Income:</strong> Your gross income after tax, including any partner income
                and other regular income sources. Tax is calculated using current{" "}
                <a
                  href="https://www.ato.gov.au/tax-rates-and-codes/tax-rates-australian-residents"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent hover:underline"
                >
                  ATO tax rates
                </a>.
              </li>
              <li>
                <strong>Living expenses:</strong> Banks use the Household Expenditure Measure (HEM)
                benchmark, which estimates minimum living costs based on household size and location.
              </li>
              <li>
                <strong>Existing debts:</strong> Credit cards are assessed at 3% of the limit monthly,
                plus any existing loan repayments and{" "}
                <a
                  href="https://www.ato.gov.au/individuals-and-families/study-and-training-support-loans/compulsory-repayments"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent hover:underline"
                >
                  HECS/HELP obligations
                </a>.
              </li>
              <li>
                <strong>Serviceability buffer:</strong>{" "}
                <a
                  href="https://www.apra.gov.au/news-and-publications/apra-increases-banks%E2%80%99-loan-serviceability-expectations-to-counter-rising"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent hover:underline"
                >
                  APRA
                </a>{" "}
                requires banks to assess your ability to repay at a rate 3% higher than the actual rate.
              </li>
            </ul>

            <div className="mt-6 p-4 bg-primary/5 rounded-lg">
              <h3 className="font-semibold text-foreground mb-2">About Living Expenses (HEM)</h3>
              <p className="text-sm mb-2">
                The <strong>Household Expenditure Measure (HEM)</strong> is developed by the{" "}
                <a
                  href="https://melbourneinstitute.unimelb.edu.au/research/reports/household-expenditure-measure"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent hover:underline"
                >
                  Melbourne Institute
                </a>{" "}
                using data from the{" "}
                <a
                  href="https://www.abs.gov.au/statistics/economy/finance/household-expenditure-survey-australia-summary-results/2015-16"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent hover:underline"
                >
                  ABS Household Expenditure Survey
                </a>. It represents the median spend on absolute basics plus a 25th percentile spend on
                discretionary items.
              </p>
              <p className="text-sm mb-2">
                HEM benchmarks vary by:
              </p>
              <ul className="text-sm list-disc pl-5 space-y-1">
                <li><strong>Household type:</strong> Single (~$1,650/mo metro) or Couple (~$2,550/mo metro)</li>
                <li><strong>Dependents:</strong> ~$450/month per child (metro)</li>
                <li><strong>Location:</strong> Metro areas have higher benchmarks than regional</li>
              </ul>
              <p className="text-sm mt-2">
                Lenders use the <em>higher</em> of your declared expenses or HEM.
              </p>
            </div>

            <div className="mt-6 grid md:grid-cols-2 gap-6">
              <p className="text-sm">
                <strong>Note:</strong> This calculator provides estimates only. Actual borrowing
                capacity varies by lender and depends on additional factors including credit history,
                employment type, and property details.
              </p>
              <p className="text-sm text-muted-foreground">
                <strong>Disclaimer:</strong> This page provides general information only and has been
                prepared without considering your objectives, financial situation or needs. We recommend
                that you consider whether it is appropriate for your circumstances and your full financial
                situation will need to be reviewed prior to acceptance of any offer or product. It does
                not constitute legal, tax or financial advice and you should always seek professional
                advice in relation to your individual circumstances.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
